import { intersection, isEmpty, union } from "lodash";
import AppContext from "PFApp/app_context";
import { getRowFromDataItem } from "PFApp/booking/parts/helpers";
import { Row } from "PFApp/booking/types";
import { useCallback, useContext } from "react";

import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { RowChildrenItem } from "../queries/use_row_children";
import { useUpdateChildrenStateUsingExistingRows } from "./use_update_children_state_using_existing_rows";

interface UseInsertChildrenRows {
  row: Row;
}

interface UseInsertChildrenRowsReturn {
  (childrenRows: RowChildrenItem["entries"]): void;
}

interface MapChildrenRowsToRowFormat {
  (childrenRows: RowChildrenItem["entries"]): {
    newChildrenRows: Row[];
    newChildrenRowKeys: string[];
  };
}

const isRowClosestParent = (key: string, parents: Row["parents"]): boolean =>
  !!parents?.length && parents?.[parents.length - 1] === key;
const findChildrenKeysForParents = (rows: Row[], parentKeys: string[]): string[] =>
  rows
    .filter(({ parents: rowParents }) => !isEmpty(intersection(rowParents, parentKeys)))
    .map(({ key }) => key);

export const useInsertChildrenRows = ({ row }: UseInsertChildrenRows): UseInsertChildrenRowsReturn => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { calendarMode } = useBookingsOverviewContext();
  const { setRowsByCalendarMode } = useBookingRowsContext();
  const { key } = row;
  const updateChildrenStateUsingExistingRows = useUpdateChildrenStateUsingExistingRows({
    parentKey: key
  });

  const mapChildrenRowsToRowFormat: MapChildrenRowsToRowFormat = useCallback(
    (childrenRows) => {
      const newChildrenRows: Row[] = childrenRows.map((item) =>
        getRowFromDataItem(item, currentProfile, calendarMode, [...row.parents, row.key])
      );
      const newChildrenRowKeys = newChildrenRows.map((i) => i.key);
      return {
        newChildrenRows,
        newChildrenRowKeys
      };
    },
    [currentProfile, calendarMode, row]
  );

  const replaceOrInsertChildrenRows = useCallback(
    (
      rows: Row[],
      newChildrenRows: Row[],
      newChildrenRowKeys: string[]
    ): { newRows: Row[]; newChildrenRows: Row[] } => {
      let insertIndex = rows.findIndex(({ key: rowKey }) => rowKey === key);
      if (insertIndex < 0) {
        return filterOutChildrenRowsIfNeeded(rows, rows, newChildrenRowKeys);
      }
      let newRows = [...rows];
      newChildrenRows.forEach((childRow) => {
        const childRowExistingIndex = newRows.findIndex((row) => row.key === childRow.key);
        if (childRowExistingIndex > -1) {
          newRows.splice(childRowExistingIndex, 1, childRow);
          insertIndex = childRowExistingIndex;
        } else {
          newRows = [...newRows.slice(0, insertIndex + 1), childRow, ...newRows.slice(insertIndex + 1)];
          insertIndex++;
        }
      });
      return filterOutChildrenRowsIfNeeded(rows, newRows, newChildrenRowKeys);
    },
    [key]
  );

  const filterOutChildrenRowsIfNeeded = useCallback(
    (
      rows: Row[],
      newRows: Row[],
      newChildrenRowKeys: string[]
    ): { newRows: Row[]; newChildrenRows: Row[] } => {
      // get children of current row (only first level of nesting) and check if some of them should be removed
      const oldClosestChildrenKeys = rows
        .filter((i) => isRowClosestParent(key, i.parents || []))
        .map((i) => i.key);
      const childrenKeysToRemove = oldClosestChildrenKeys.filter(
        (rowKey) => !newChildrenRowKeys.includes(rowKey)
      );
      // in case there are children to be removed, find also their nested children to remove whole branch
      const nestedChildrenKeysToRemove = findChildrenKeysForParents(rows, childrenKeysToRemove);
      const keysToRemove = union(childrenKeysToRemove, nestedChildrenKeysToRemove);
      const newChildrenKeys = newChildrenRowKeys.filter((rowKey) => !keysToRemove.includes(rowKey));
      const newRowsFiltered = newRows.filter((i) => !keysToRemove.includes(i.key));
      return {
        newRows: newRowsFiltered,
        newChildrenRows: newRowsFiltered.filter(({ key }) => newChildrenKeys.includes(key))
      };
    },
    [key]
  );

  return useCallback(
    (childrenRows) => {
      setRowsByCalendarMode((prevState) => {
        const prevRows = prevState[calendarMode];
        const { newChildrenRows, newChildrenRowKeys } = mapChildrenRowsToRowFormat(childrenRows);
        const prevExistingChildrenRows = prevRows.filter(({ key }) => newChildrenRowKeys.includes(key));
        const { newRows: allRowsWithChildrenFiltered, newChildrenRows: newChildrenRowsFiltered } =
          replaceOrInsertChildrenRows(prevRows, newChildrenRows, newChildrenRowKeys);

        updateChildrenStateUsingExistingRows(newChildrenRowsFiltered, prevExistingChildrenRows);
        return {
          ...prevState,
          [calendarMode]: allRowsWithChildrenFiltered
        };
      });
    },
    [
      calendarMode,
      setRowsByCalendarMode,
      mapChildrenRowsToRowFormat,
      replaceOrInsertChildrenRows,
      updateChildrenStateUsingExistingRows
    ]
  );
};
